import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { CollaborationState, OpenEstimateRequest, Preferences } from '../../../models/dtos';
import { Estimate, EstimateGroup } from '../../../models/common/RecentAndFavEstimate';
import { firstValueFrom } from 'rxjs';
import { CONSTANTS } from '../../../Constants/Constants';
import { CommonDataService } from '../../../services/common-data.service';
import { EstimateCloseService } from '../../../services/estimate-close.service';
import { EstimateSaveService } from '../../../services/estimate-save.service';
import { PreferencesService } from '../../../services/preferences/preferences.service';
import { UserProfileService } from '../../../services/common/user-profile.service';
import { OpenEstimateApiService } from '../../../services/api/open-estimate.service';
import { OpenEstimateOperationsService } from '../../../services/common/open-estimate-operations.service';
import { SaveConfirmationDialogComponent } from '../../dialogs/common/save-confirmation-dialog/save-confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MultilingualDatePipe } from '../../../pipes/multilingual-date-pipe/multilingual-date-pipe';
import { GocCollaborationRestrictionHandlerService } from '../../../services/common/goc-collaboration-restriction-handler.service';
import { NotificationMessageService } from '../../../services/common/notification-message.service';


@Component({
  selector: 'app-recent-and-fav-estimate',
  standalone: true,
  imports: [CommonModule,
    MatIconModule,
    TranslatePipe,
    MultilingualDatePipe
  ],
  templateUrl: './recent-and-fav-estimate.component.html',
  styleUrl: './recent-and-fav-estimate.component.scss'
})
export class RecentAndFavEstimateComponent implements OnInit {

  @Output() navigateToEstimateTab = new EventEmitter<void>();

  allRecentEstimates: Estimate[] = [];

  recentEstimates: Estimate[] = [];
  favoriteEstimates: Estimate[] = [];
  maxRecentDisplay: number = CONSTANTS.TWELVE;
  maxFavouriteDisplay: number = CONSTANTS.FOUR;
  recentEstimateGroups: EstimateGroup[] = [];
  preferenceData: Preferences = null;
  favoriteEstimateIds: string[] = [];

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly preferencesService: PreferencesService,
    private readonly estimateCloseOperationsService: EstimateCloseService,
    private readonly estimateSaveService: EstimateSaveService,
    private readonly openEstimateApiService: OpenEstimateApiService,
    private readonly userProfileService: UserProfileService,
    private readonly openEstimateOperationsService: OpenEstimateOperationsService,
    private readonly dialog: MatDialog,
    private readonly notificationMessageService: NotificationMessageService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService) { }

  ngOnInit(): void {
    this.loadSettings();
    this.updateDisplayedEstimates();
  }

  loadSettings(): void {
    this.preferenceData = this.commonDataService.getPreferenceData()?.preferences;
    if (this.preferenceData) {
      this.maxRecentDisplay = this.preferenceData.mostRecentlyOpenedEstimateListMaxItemCount || CONSTANTS.TWELVE;
      this.maxFavouriteDisplay = this.preferenceData.favoriteEstimateMaxItemCount || CONSTANTS.FOUR;
      this.allRecentEstimates = this.preferenceData.recentFilesList ? JSON.parse(this.preferenceData.recentFilesList) as Estimate[] : [];
      this.favoriteEstimates = this.preferenceData.favoriteEstimate ? JSON.parse(this.preferenceData.favoriteEstimate) as Estimate[] : [];
    }
  }

  updateDisplayedEstimates() {
    const favoriteIds = new Set(this.favoriteEstimates.map(e => e.id.trim()));
    this.recentEstimates = this.allRecentEstimates
      .filter(e => !favoriteIds.has(e.id))
      .slice(0, this.maxRecentDisplay);
    this.groupRecentEstimates();
  }

  moveToFavorite(estimate: Estimate) {
    if (this.favoriteEstimates.length < this.maxFavouriteDisplay) {
      this.recentEstimates = this.recentEstimates.filter(e => e.id !== estimate.id);
      this.updateFavoritesAndSave(estimate);
    } else if (this.recentEstimates.length < this.maxRecentDisplay) {
      const removedFavorite = this.favoriteEstimates.pop();
      this.recentEstimates = [removedFavorite, ...this.recentEstimates];
      this.recentEstimates = this.recentEstimates.filter(e => e.id !== estimate.id);
      this.updateFavoritesAndSave(estimate);
    } else {
      this.favoriteEstimates.pop();
      this.updateFavoritesAndSave(estimate);
    }
  }

  moveToRecent(estimate: Estimate) {
    this.favoriteEstimates = this.favoriteEstimates.filter(e => e.id !== estimate.id);
    const next = [estimate, ...this.recentEstimates.filter(e => e.id !== estimate.id)];
    this.recentEstimates = next.slice(0, this.maxRecentDisplay);
    this.preferencesService.savePreferences(this.preferenceData, this.favoriteEstimates, this.recentEstimates);
    this.groupRecentEstimates();
  }

  updateFavoritesAndSave(estimate: Estimate) {
    this.favoriteEstimates = [...this.favoriteEstimates, estimate];
    this.preferencesService.savePreferences(this.preferenceData, this.favoriteEstimates, this.recentEstimates);
    this.groupRecentEstimates();
  }

  trackById(item: number): number {
    return item;
  }


  private setToMidnight(date: Date): Date {
    const midnight = new Date(date);
    midnight.setHours(0, 0, 0, 0);
    return midnight;
  }

  groupRecentEstimates() {
    const groups: EstimateGroup[] = [];
    const today = new Date();
    const todayMidnight = this.setToMidnight(today);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const yesterdayMidnight = this.setToMidnight(yesterday);

    const startOfWeek = new Date(todayMidnight);
    startOfWeek.setDate(todayMidnight.getDate() - todayMidnight.getDay());

    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfWeek.getDate() - 7);

    const endOfLastWeek = new Date(startOfWeek);
    endOfLastWeek.setDate(startOfWeek.getDate() - 1);

    const todayEstimates = [];
    const yesterdayEstimates = [];
    const thisWeekEstimates = [];
    const lastWeekEstimates = [];
    const olderEstimates = [];

    for (const estimate of this.recentEstimates) {
      const estDate = this.setToMidnight(new Date(estimate.date));

      if (estDate.getTime() === todayMidnight.getTime()) {
        todayEstimates.push(estimate);
      } else if (estDate.getTime() === yesterdayMidnight.getTime()) {
        yesterdayEstimates.push(estimate);
      } else if (estDate >= startOfWeek && estDate < todayMidnight) {
        thisWeekEstimates.push(estimate);
      } else if (estDate >= startOfLastWeek && estDate <= endOfLastWeek) {
        lastWeekEstimates.push(estimate);
      } else {
        olderEstimates.push(estimate);
      }
    }

    const groupLabels = [
      { label: 'TODAY', estimates: todayEstimates },
      { label: 'YESTERDAY', estimates: yesterdayEstimates },
      { label: 'THIS_WEEK', estimates: thisWeekEstimates },
      { label: 'LAST_WEEK', estimates: lastWeekEstimates },
      { label: 'OLDER', estimates: olderEstimates }
    ];

    for (const group of groupLabels) {
      if (group.estimates.length) groups.push({ label: group.label, estimates: group.estimates });
    }

    this.recentEstimateGroups = groups;
  }

  async onEstimateClick(estimate: Estimate) {
    const canOpen = await this.handleAnyUnsavedChanges();
    if (!canOpen) {
      return;
    }
    if (!estimate?.id) {
      return;
    }
    const request: OpenEstimateRequest = {
      userId: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
      estimateId: estimate.id,
      userEmailId: this.userProfileService.userInfo?.mail ?? CONSTANTS.EMPTY_STRING,
      culture: this.commonDataService.getCurrentApplicationCulture(),
      isArchive: false
    }
    // Do NOT tie this to a destroy subject: handleAnyUnsavedChanges() has
    // already closed the current estimate, so the request must complete even
    // if this component is torn down by the reactive cascade from
    // resetEstimateState().

    this.openEstimateApiService.openEstimate(request).subscribe({
      next: async (response) => {
        if (response) {
          if (response.showGOCAccessWarning) {
            this.notificationMessageService.showWarning('GOC_ACCESS_WARNING_MESSAGE', 'RESTRICTED');
          }
          await this.openEstimateOperationsService.openEstimate(response);
          this.navigateToEstimateTab.emit();
          // Root-scoped fallback: fires even if this component was destroyed
          // by the resetEstimateState() cascade before the response arrived.
          this.commonDataService.requestNavigateToEstimateTab();
        }
      },
      error: async (err) => {
        if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(err)) {
          return;
        }
        console.error('Error opening estimate:', err);
      }
    });

  }

  async handleAnyUnsavedChanges(): Promise<boolean> {
    if (this.commonDataService.getCollaborationState() === CollaborationState.Edit) {
      if (!await this.estimateCloseOperationsService.collaborativeChecksBeforeClose(
        () => this.estimateSaveService.createSaveEstimateRequest())) {
        return false;
      }
      await this.estimateCloseOperationsService.closeEstimate();
      return true;
    }
    if (this.commonDataService.isSaveEnabled && !this.commonDataService.isReadOnlyMode()) {
      const confirmationDialogRef = this.dialog.open(SaveConfirmationDialogComponent, {
        disableClose: true,
        data: { estimateName: this.commonDataService.getEstimateName() }
      });
      const okClicked = await firstValueFrom(confirmationDialogRef.afterClosed());
      if (okClicked === null || okClicked === undefined) {
        return false;
      }
      if (okClicked) {
        await this.estimateSaveService.save();
      }
      this.commonDataService.isSaveEnabled = false;
    }
    await this.estimateCloseOperationsService.closeEstimate();
    return true;
  }
}
